import React from "react";
import { motion } from "framer-motion";
import { 
  Building2, 
  Award, 
  Microscope, 
  TrendingUp, 
  Landmark 
} from "lucide-react";
import { 
  fadeInUp, 
  staggerContainer, 
  scaleIn 
} from '../utils/animations';
import { useAdvancedScrollAnimation } from '../hooks/useAdvancedScrollAnimation';

const ResearchCenters = () => {
  const { sectionRef, triggerRef, isMobile } = useAdvancedScrollAnimation({
    fadeOutOnLeave: true,
    mobileBreakpoint: 768,
    fadeThreshold: 0.3
  });

  return (
    <div ref={triggerRef} className="w-full">
      <motion.section 
        ref={sectionRef}
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className={`
          py-16 md:py-20 
          bg-gray-50 
          relative 
          overflow-hidden 
          ${isMobile ? 'px-4' : ''}
        `}
      >
        <div className="container mx-auto px-4">
          <motion.h2 
            className={`
              font-bold text-center mb-12 md:mb-16 text-gray-900
              ${isMobile 
                ? 'text-2xl sm:text-3xl' 
                : 'text-3xl md:text-4xl'
              }
            `}
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            Our Research Centers
          </motion.h2>

          <motion.div 
            className={`
              grid 
              ${isMobile 
                ? 'grid-cols-1 gap-6' 
                : 'grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8'
              } 
              mb-16
            `}
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <ResearchCard
              icon={<Microscope />}
              title="Centre for Advanced Technologies"
              description="Pioneering cutting-edge technological research and innovation across multiple domains."
            />
            <ResearchCard
              icon={<TrendingUp />}
              title="Centre for Economic Research"
              description="Conducting comprehensive economic analysis and developing innovative policy solutions."
            />
            <ResearchCard
              icon={<Landmark />}
              title="Centre for Historical Studies"
              description="Exploring historical narratives and their contemporary relevance through rigorous research."
            />
          </motion.div>

          <div className="policy-section">
            <motion.h3 
              className={`
                font-bold text-center mb-10 text-gray-900
                ${isMobile 
                  ? 'text-xl' 
                  : 'text-2xl md:text-3xl'
                }
              `}
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              Policy Research Initiatives
            </motion.h3>
            
            <motion.div 
              className={`
                grid 
                ${isMobile 
                  ? 'grid-cols-1 gap-6' 
                  : 'grid-cols-2 gap-6 md:gap-8'
                }
              `}
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <PolicyCard
                icon={<Building2 />}
                title="Centre for Statistics and Analysis"
                description="Transforming raw data into actionable insights through advanced statistical methodologies."
              />
              <PolicyCard
                icon={<Award />}
                title="Centre for Sustainable Development"
                description="Driving innovative approaches to global sustainability and environmental stewardship."
              />
            </motion.div>
          </div>
        </div>

        {/* Animated background shapes */}
        <motion.div
          className="absolute w-96 h-96 bg-amber-100/30 rounded-full -right-48 -bottom-48"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      </motion.section>
    </div>
  );
};

// Research Card Component with Mobile Responsiveness
const ResearchCard = ({ icon, title, description }) => {
  return (
    <motion.div
      className={`
        bg-white 
        p-4 md:p-6 lg:p-8 
        rounded-xl 
        shadow-lg 
        hover:shadow-xl 
        transition-all 
        duration-300
      `}
      variants={scaleIn}
      whileHover={{ 
        scale: 1.05,
        rotate: 0,
        y: -10
      }}
    >
      <motion.div
        className="text-amber-500 mb-3 md:mb-4"
        whileHover={{ rotate: 360 }}
        transition={{ duration: 0.6 }}
      >
        {React.cloneElement(icon, { size: 36 })}
      </motion.div>
      <h4 className="text-base md:text-lg font-semibold text-gray-800 mb-2">{title}</h4>
      <p className="text-gray-600 text-sm">{description}</p>
    </motion.div>
  );
};

// Policy Card Component with Mobile Responsiveness
const PolicyCard = ({ icon, title, description }) => {
  return (
    <motion.div
      className={`
        bg-white 
        p-4 md:p-6 
        rounded-lg 
        shadow-lg 
        hover:shadow-xl 
        transition-all 
        duration-300
      `}
      variants={scaleIn}
      whileHover={{ scale: 1.05 }}
    >
      <div className="flex items-start space-x-4">
        <motion.div 
          className="text-amber-500 flex-shrink-0"
          whileHover={{ rotate: 360 }}
          transition={{ duration: 0.6 }}
        >
          {React.cloneElement(icon, { size: 28 })}
        </motion.div>
        <div>
          <h4 className="text-base md:text-lg font-semibold mb-2 text-gray-800">{title}</h4>
          <p className="text-gray-600 text-sm">{description}</p>
        </div>
      </div>
    </motion.div>
  );
};

export default ResearchCenters;